import type { APMAsset, ICDFView } from '@cognite/apm-client';
import { VIEW_VERSIONS_V10 } from '@cognite/apm-client';
import type {
  CogniteClient,
  Datapoints,
  ExternalDatapoint,
  Timeseries,
  TimeseriesFilter,
} from '@cognite/sdk';

import { DatapointService } from './datapoint-service';
import { timeseriesFilterToFdmFilter } from './timeseries-idm-migrator';
import type { ITimeseriesService } from './timeseries-service-interface';
import { filterDatapointStrings, filterTimeseriesStrings } from './utils';

export type CogniteClientDependencies = {
  timeseries: Pick<
    CogniteClient['timeseries'],
    'retrieve' | 'search' | 'list' | 'update' | 'create'
  >;
  datapoints: Pick<
    CogniteClient['datapoints'],
    'insert' | 'delete' | 'retrieveLatest' | 'retrieve'
  >;
  relationships: Pick<CogniteClient['relationships'], 'list'>;
  post: CogniteClient['post'];
  project: string;
  instances: Pick<CogniteClient['instances'], 'upsert'>;
};

export type APMClientDependencies = {
  timeseries?: Pick<
    ICDFView<Timeseries>,
    'byId' | 'list' | 'search' | 'defaultInstanceSpace' | 'viewReference'
  >;
  assets: Pick<ICDFView<APMAsset>, 'defaultInstanceSpace'>;
  appDataInstanceSpace?: string;
  sourceDataInstanceSpace?: string;
};

export class TimeseriesService implements ITimeseriesService {
  client;
  apmClient;
  datapointService;
  spaces: string[];
  constructor(
    client: CogniteClientDependencies,
    apmClient: APMClientDependencies
  ) {
    this.spaces = [];

    if (apmClient.appDataInstanceSpace) {
      this.spaces.push(apmClient.appDataInstanceSpace);
    }

    if (apmClient.sourceDataInstanceSpace) {
      this.spaces.push(apmClient.sourceDataInstanceSpace);
    }

    this.client = client;
    this.apmClient = apmClient;
    this.datapointService = new DatapointService(client);
  }

  async getTimeseriesByExternalId(externalId: string) {
    if (this.apmClient.timeseries) {
      const [timeseriesData] = await this.apmClient.timeseries.byId([
        { externalId },
      ]);
      return timeseriesData;
    }

    const [timeseriesData] = await this.client.timeseries.retrieve(
      [{ externalId }],
      { ignoreUnknownIds: true }
    );
    return timeseriesData;
  }

  async upsertTimeseries(
    timeseries: Partial<Timeseries>,
    rootAssetId?: number
  ) {
    let timeseriesData;
    const assetId = timeseries.assetId || rootAssetId;

    if (timeseries.id) {
      // since metadata is completely overwritten we need to get the original timeseries metadata
      const originalTimeseries = await this.getTimeseriesByExternalId(
        timeseries.externalId!
      );

      const isCdmManaged = Boolean(originalTimeseries?.instanceId);

      timeseriesData = await this.client.timeseries.update([
        {
          externalId: timeseries.externalId || '',
          update: {
            ...(!isCdmManaged && {
              name: { set: timeseries.name || '' },
              description: { set: timeseries.description || '' },
              unit: { set: timeseries.unit || '' },
            }),
            metadata: {
              set: {
                ...originalTimeseries?.metadata,
                ...timeseries.metadata,
                source: 'APP',
              },
            },
            assetId: assetId ? { set: assetId } : undefined,
          },
        },
      ]);

      if (originalTimeseries?.instanceId) {
        await this.upsertTimeseriesViaDM(
          originalTimeseries.instanceId,
          timeseries
        );
      }
    } else {
      timeseriesData = await this.client.timeseries.create([
        {
          ...timeseries,
          assetId: timeseries.assetId || rootAssetId,
          metadata: {
            source: 'APP',
          },
        },
      ]);
    }

    return timeseriesData;
  }

  /**
   * Updates name and description on a CDM-managed timeseries via the DM instances API.
   * This is only used in classic/hybrid projects (full CDM projects use CdmTimeseriesService).
   */
  async upsertTimeseriesViaDM(
    instanceId: { space: string; externalId: string },
    timeseries: Partial<Timeseries>
  ) {
    const properties = getCdmManagedFields(timeseries);

    if (Object.keys(properties).length === 0) {
      return;
    }

    const viewReference = {
      type: 'view' as const,
      externalId: 'CogniteTimeSeries',
      space: 'cdf_cdm',
      version: VIEW_VERSIONS_V10.TIMESERIES,
    };

    await this.client.instances.upsert({
      items: [
        {
          instanceType: 'node',
          externalId: instanceId.externalId,
          space: instanceId.space,
          sources: [
            {
              source: viewReference,
              properties,
            },
          ],
        },
      ],
    });
  }

  /**
   * insertDataPoint
   * Add datapoint is not available in asset explorer.
   * Therefore we don't need to support IDM for now.
   */
  async insertDataPoint(
    timeseriesExternalId: string,
    dataPoint: ExternalDatapoint
  ) {
    return this.client.datapoints.insert([
      { externalId: timeseriesExternalId, datapoints: [dataPoint] },
    ]);
  }

  /**
   * deleteDataPoint
   * Delete datapoint is not available in asset explorer.
   * Therefore we don't need to support IDM for now.
   */
  async deleteDataPoint(timeseriesExternalId: string, timestamp: number) {
    return this.client.datapoints.delete([
      {
        externalId: timeseriesExternalId,
        inclusiveBegin: timestamp,
        exclusiveEnd: timestamp + 1,
      },
    ]);
  }

  async getTimeseriesByAsset(
    assetExternalId: string,
    filter: TimeseriesFilter = {},
    limit?: number
  ) {
    const finalFilters: TimeseriesFilter = {
      assetExternalIds: [assetExternalId],
      isString: false,
      ...filter,
    };

    if (this.apmClient.timeseries) {
      const { items } = await this.apmClient.timeseries.list({
        filters: timeseriesFilterToFdmFilter(
          this.apmClient.assets.defaultInstanceSpace,
          finalFilters
        ),
        spaces: this.spaces,
        pageSize: limit,
      });

      return items;
    }

    const { items } = await this.client.timeseries.list({
      filter: finalFilters,
      limit,
    });
    return items;
  }

  async getTimeseriesByRelationships(assetExternalId: string, limit?: number) {
    // In IDM we will not use relationships to get timeseries
    if (this.apmClient.timeseries) {
      return [];
    }

    let timeseriesByRelationships: Timeseries[] = [];
    const relationships = await this.client.relationships
      .list({
        filter: {
          sourceTypes: ['timeSeries'],
          targetExternalIds: [assetExternalId],
          targetTypes: ['asset'],
        },
        limit,
      })
      .autoPagingToArray({ limit });

    if (relationships.length > 0) {
      const uniqueTimeseriesIds = [
        ...new Set(
          relationships.map((relationship) => relationship.sourceExternalId)
        ),
      ];
      timeseriesByRelationships = await this.client.timeseries.retrieve(
        uniqueTimeseriesIds.map((externalId) => ({ externalId })),
        {
          ignoreUnknownIds: true,
        }
      );
    }

    return filterTimeseriesStrings(timeseriesByRelationships);
  }

  // the max limit for client.datapoints is 100 .retrieveLatest
  async getTimeseriesLatestDatapoints(
    timeseries: Timeseries[]
  ): Promise<Datapoints[]> {
    const externalIds = timeseries.flatMap((ts) =>
      ts.externalId ? [ts.externalId] : []
    );
    if (this.apmClient.timeseries) {
      const datapoint = await this.datapointService.retrieveLatest(
        externalIds,
        this.apmClient.timeseries.defaultInstanceSpace
      );
      return filterDatapointStrings(datapoint);
    }

    const datapoints = await this.client.datapoints.retrieveLatest(
      externalIds.map((externalId) => ({
        externalId,
      })),
      {
        ignoreUnknownIds: true,
      }
    );

    return filterDatapointStrings(datapoints);
  }

  async searchTimeseries(
    query: string,
    limit?: number,
    filters?: TimeseriesFilter
  ) {
    if (this.apmClient.timeseries) {
      const timeseries = await this.apmClient.timeseries.search({
        query,
        filters: timeseriesFilterToFdmFilter(
          this.apmClient.assets.defaultInstanceSpace,
          filters,
          true
        ),
        searchOperator: 'AND',
        spaces: this.spaces,
        pageSize: limit,
      });

      return timeseries;
    }

    const timeseries = await this.client.timeseries.search({
      search: {
        query,
      },
      filter: filters,
      limit,
    });
    return timeseries;
  }
}

export const createTimeseriesService = (
  ...args: ConstructorParameters<typeof TimeseriesService>
): ITimeseriesService => new TimeseriesService(...args);

function getCdmManagedFields(
  timeseries: Partial<Timeseries>
): Record<string, string> {
  const properties: Record<string, string> = {};
  if (timeseries.name !== undefined) {
    properties.name = timeseries.name;
  }
  if (timeseries.description !== undefined) {
    properties.description = timeseries.description;
  }
  if (timeseries.unit !== undefined) {
    properties.unit = timeseries.unit;
  }
  return properties;
}
